import { useRef, useState } from 'react';

/**
 * Native HTML5 file drag-drop hook for CSV uploads.
 * Uses a ref-counted enter/leave counter so dragleave on child elements
 * doesn't flicker the drop-target styling.
 *
 * `onError` is invoked with a Dutch error message when the drop is rejected
 * (multiple files, non-CSV). The consumer decides how to surface it — e.g.
 * MateriaalTab writes it to the project-view store.
 */
export function useCsvFileDrop({
  enabled,
  onFile,
  onError,
}: {
  enabled: boolean;
  onFile: (file: File) => void;
  onError: (msg: string) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const reset = () => {
    dragCounter.current = 0;
    setIsDragOver(false);
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    // Only react if the drag actually contains files (not text/in-app drags)
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragOver(true);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    // preventDefault is required to allow drop
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragOver(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enabled) return;
    e.preventDefault();
    reset();

    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    if (files.length > 1) {
      onError('Sleep één CSV-bestand tegelijk.');
      return;
    }
    const file = files[0];
    const isCsv =
      file.name.toLowerCase().endsWith('.csv') ||
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel';
    if (!isCsv) {
      onError('Alleen CSV-bestanden worden geaccepteerd.');
      return;
    }
    onFile(file);
  };

  return {
    isDragOver,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
